import React, { useState } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Tooltip,
  IconButton,
  FormErrorMessage,
} from '@chakra-ui/react';
import { InfoIcon, AddIcon } from '@chakra-ui/icons';

const AddSheet = ({ onAdd, disabled }) => {
  const [sheetUrl, setSheetUrl] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const parseGoogleSheetUrl = (url) => {
    try {
      // First try to find gid in the URL (either after ? or #)
      const gidRegex = /[?#].*gid=([0-9]+)/;
      const gidMatch = url.match(gidRegex);
      const gid = gidMatch ? gidMatch[1] : '0';

      // Then extract the sheet ID
      const sheetIdRegex = /\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/;
      const sheetIdMatch = url.match(sheetIdRegex);
      
      if (!sheetIdMatch) {
        throw new Error('Invalid Google Sheets URL');
      }

      return {
        sheetId: sheetIdMatch[1],
        gid
      };
    } catch (error) {
      throw new Error('Please enter a valid Google Sheets URL');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!sheetUrl.trim()) {
      setError('Sheet URL is required');
      return;
    }

    if (!name.trim()) {
      setError('Sheet name is required');
      return;
    }

    try {
      const { sheetId, gid } = parseGoogleSheetUrl(sheetUrl.trim());
      await onAdd({ sheetId, gid, name: name.trim() });
      setSheetUrl('');
      setName('');
      setError('');
    } catch (error) {
      setError(error.message);
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <Heading size="md" mb={4}>
        Add New Sheet
      </Heading>

      <VStack spacing={4} align="stretch">
        <FormControl isInvalid={!!error}>
          <FormLabel>Sheet Name</FormLabel>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter a name for this sheet"
            isDisabled={disabled}
          />
        </FormControl>

        <FormControl isInvalid={!!error}>
          <FormLabel>
            <HStack spacing={1}>
              <Text>Google Sheet URL</Text>
              <Tooltip
                label="Paste the full URL of your Google Sheet. The Sheet ID and GID will be automatically extracted."
                hasArrow
              >
                <IconButton
                  icon={<InfoIcon />}
                  size="xs"
                  variant="ghost"
                  aria-label="URL Info"
                />
              </Tooltip>
            </HStack>
          </FormLabel>
          <Input
            value={sheetUrl}
            onChange={(e) => setSheetUrl(e.target.value)}
            placeholder="https://docs.google.com/spreadsheets/d/..."
            isDisabled={disabled}
          />
          {error && <FormErrorMessage>{error}</FormErrorMessage>}
        </FormControl>

        <Button
          type="submit"
          colorScheme="blue"
          leftIcon={<AddIcon />}
          isDisabled={disabled || !sheetUrl.trim() || !name.trim()}
          isLoading={disabled}
          loadingText="Adding"
          alignSelf="flex-start"
        >
          Add Sheet
        </Button>

        <Text fontSize="sm" color="gray.500">
          Paste the complete Google Sheets URL and we'll automatically extract the necessary information.
        </Text>
      </VStack>
    </Box>
  );
};

export default AddSheet;
